import React, { useContext, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { EditPanelContext } from 'app/editor/report/content-editor/block-edit-panel/BlockEditPanel';
import useBundleTranslation from 'i18n';
import { reportAPI } from 'api/report';
import { Box, Divider, Stack } from '@mui/material';
import BlockEditPanelHeader from 'app/editor/report/content-editor/block-edit-panel/BlockEditPanelHeader';
import BlockEditPanelControls from 'app/editor/report/content-editor/block-edit-panel/BlockEditPanelControls';
import EditPanelCheckBox from 'app/editor/report/content-editor/block-edit-panel/edit-panel-components/EditPanelCheckBox';
import { FormDataAPIType, RawFormElementProps } from 'components/common/form';
import useCustomSimplifiedForm from 'components/common/form/hooks/useCustomSimplifiedForm';
import { AssocArray, YNValues } from 'tools/types';
import { escapeMarkdown } from 'tools/escapeMarkdown';
import ReactHookFormController, { TemplateFormComponent } from 'components/common/form/layout/ReactHookFormController';
import { prepareFormElementProps } from 'components/common/form/formTools';
import GetEmbedCode from 'app/editor/report/content-editor/block-edit-panel/edit-panel-components/GetEmbedCode';
import ReportContentSkeletonPlaceholder from 'components/report-content/utils/ReportContentSkeletonPlaceholder';
import {
    BlocksContext,
    BlockType,
    ComponentUpdateProps,
    ComponentSettingsLlm,
    IBlocksContext,
    llmAiInsightDefaults,
} from '@report-content';
import EditPanelLabel from 'app/editor/report/content-editor/block-edit-panel/edit-panel-components/EditPanelLabel';

// Field for form
const componentFormFields: Array<keyof ComponentSettingsLlm> = [
    'title',
    'resources',
    'prompt',
    'primary_resource',
    'only_when_significant_ind',
    'fallback_message',
    'generation_mode',
    'show_in_viewer',
    'include_in_email',
];
const componentNumericFormField: Array<keyof ComponentSettingsLlm> = ['height'];

export default function LlmEditPanel({
    component,
    block,
    contentSettings,
}: ComponentUpdateProps<ComponentSettingsLlm> & { block: BlockType<ComponentSettingsLlm> }) {
    // 'app/editor/element/extreport' reuse the AI Insights copy instead of duplicating it.
    const { t } = useBundleTranslation(['components/report-content', 'app/editor/element/extreport']);
    const editPanelContext = useContext(EditPanelContext);
    // The same filters the canvas posts, so Preview and the saved block answer the same question.
    const blocksContext = useContext<IBlocksContext | null>(BlocksContext);
    const filtersString = blocksContext?.filters ? JSON.stringify(blocksContext.filters) : null;

    // Prepare data for Form
    const defaultState: AssocArray<any> = {};
    // Add simple fields to form
    // Fall back to defaults for fields older, already-saved blocks won't have in settings.
    componentFormFields.forEach(
        (f) => (defaultState[f] = component.settings[f] ?? (llmAiInsightDefaults as AssocArray<any>)[f]),
    );
    componentNumericFormField.forEach((f) => (defaultState[f] = Number(component.settings[f])));

    const elementProps: FormDataAPIType = useCustomSimplifiedForm(defaultState);

    // InsightPreviewInline/FormStaticLabel resolve translations from this
    // namespace — reuse the ExtReport AI Insight bundle instead of duplicating its strings.
    const insightTestElementProps = {
        form: elementProps.form,
        translationNS: 'app/editor/element/extreport',
    } as RawFormElementProps;

    const actualHeight = elementProps.form.hookFormWatch('height');
    const onlyWhenSignificant: YNValues = elementProps.form.hookFormWatch('only_when_significant_ind') ?? 'N';
    const primaryResourceValue: string = elementProps.form.hookFormWatch('primary_resource') ?? '';

    const reportAiInsightId = Number(component.settings.report_ai_insight_id ?? 0);
    const {
        data: resourceCandidatesData,
        isLoading: isResourceCandidatesLoading,
        isFetchedAfterMount: isResourceCandidatesFetched,
        isError: isResourceCandidatesError,
    } = useQuery({
        queryKey: ['llm-ai-insight-resource-candidates', contentSettings.elementId, reportAiInsightId],
        queryFn: () => reportAPI.getAiInsightResourceCandidates(contentSettings.elementId, reportAiInsightId),
        // The app disables refetch-on-mount globally, but this panel remounts on every open and
        // must reflect the latest KG admission / chosen-primary state (MI-30225), not a stale cache.
        refetchOnMount: 'always',
    });

    // Seeding only ever fills an empty field — an author's own choice (or an already-stored primary,
    // hydrated into the form's defaultState) is never overwritten once this has run. It waits for this
    // open's own successful fetch: the cached list from the previous open still names the default a cleared
    // primary replaced, and a failed refetch keeps that list while still counting as fetched (MI-30841).
    const isResourceCandidatesFresh = isResourceCandidatesFetched && !isResourceCandidatesError;
    useEffect(() => {
        if (isResourceCandidatesFresh && '' === primaryResourceValue && resourceCandidatesData?.defaultPrimary) {
            elementProps.form.hookFormSetValue('primary_resource', resourceCandidatesData.defaultPrimary.key, {
                shouldDirty: true,
            });
        }
    }, [isResourceCandidatesFresh, resourceCandidatesData?.defaultPrimary]);

    const primaryHelper = useMemo(() => {
        const defaultPrimary = resourceCandidatesData?.defaultPrimary ?? null;
        if ('' === primaryResourceValue) {
            return { text: resourceCandidatesData?.emptyPrimaryNotice ?? '', type: 'warning' as const };
        }
        // only while the field still holds the stored (deleted) key
        if (
            resourceCandidatesData?.deletedPrimaryNotice &&
            primaryResourceValue === resourceCandidatesData.chosenPrimary
        ) {
            return { text: resourceCandidatesData.deletedPrimaryNotice, type: 'warning' as const };
        }
        if (defaultPrimary && primaryResourceValue === defaultPrimary.key) {
            return {
                text: t('concierge_insight_primary_default_report_dataset', {
                    name: escapeMarkdown(defaultPrimary.label),
                }),
                type: 'info' as const,
            };
        }
        return {
            text: defaultPrimary
                ? t('concierge_insight_primary_own_choice_with_default', { name: escapeMarkdown(defaultPrimary.label) })
                : t('concierge_insight_primary_own_choice'),
            type: 'info' as const,
        };
    }, [
        primaryResourceValue,
        resourceCandidatesData?.defaultPrimary,
        resourceCandidatesData?.emptyPrimaryNotice,
        resourceCandidatesData?.deletedPrimaryNotice,
        resourceCandidatesData?.chosenPrimary,
        t,
    ]);

    // Apply Button Click save all changes to ReportState
    const handleApply = () => {
        // Process measure fields settings
        const newSettings: ComponentSettingsLlm = {
            ...component.settings,
        } as ComponentSettingsLlm;
        //@ts-ignore
        componentFormFields.forEach((f) => (newSettings[f] = elementProps.form.hookFormGetValues(f)));
        //@ts-ignore
        componentNumericFormField.forEach((f) => (newSettings[f] = Number(elementProps.form.hookFormGetValues(f))));

        const resultSettings = {
            ...newSettings,
        } as ComponentSettingsLlm;

        editPanelContext?.updateBlockSettings(block.uid, resultSettings, true);
    };

    return (
        <Stack height={1}>
            {/*Main Panel*/}
            <Box flexShrink={0}>
                <BlockEditPanelHeader
                    title={t(component.settings.isOnCreation ? 'llm.add_title' : 'llm.edit_title')}
                    onCancel={() => editPanelContext?.closeEditPanel(block.uid, component.settings)}
                />
            </Box>
            <Stack sx={{ overflowY: 'auto', overflowX: 'hidden', flexGrow: 1, p: 3 }} spacing={2}>
                <Box>
                    <ReactHookFormController
                        elementProps={prepareFormElementProps({
                            ...elementProps,
                            component: {
                                component: 'FormText',
                                name: 'title',
                                label: t('concierge_insight_name'),
                                props: { placeholder: t('concierge_insight_name_ph') },
                            },
                        })}
                    />
                </Box>
                <Box>
                    {isResourceCandidatesLoading ? (
                        <ReportContentSkeletonPlaceholder height={96} />
                    ) : (
                        <>
                            <Box mb={2}>
                                <TemplateFormComponent
                                    elementProps={elementProps}
                                    component={'PrimaryResourcePicker'}
                                    name={'primary_resource'}
                                    componentValues={resourceCandidatesData?.primaryCandidates ?? []}
                                    props={{ showLabel: true, poolEmpty: resourceCandidatesData?.poolEmpty }}
                                />
                                {primaryHelper.text && (
                                    <Box sx={{ pt: 1 }}>
                                        <TemplateFormComponent
                                            elementProps={elementProps}
                                            component={'FormStaticInfo'}
                                            name={'primary_resource_helper'}
                                            props={{ label: primaryHelper.text, type: primaryHelper.type }}
                                        />
                                    </Box>
                                )}
                            </Box>
                            <TemplateFormComponent
                                elementProps={elementProps}
                                component={'InsightResourcePicker'}
                                name={'resources'}
                                componentValues={resourceCandidatesData?.chips ?? []}
                                props={{ primaryFieldName: 'primary_resource' }}
                            />
                        </>
                    )}
                </Box>
                <Box>
                    <EditPanelLabel>{t('concierge_insight_prompt')}</EditPanelLabel>
                    <ReactHookFormController
                        elementProps={prepareFormElementProps({
                            ...elementProps,
                            component: {
                                component: 'FormTextarea',
                                name: 'prompt',
                                label: t('concierge_insight_prompt'),
                                props: { placeholder: t('concierge_insight_prompt_ph'), minRows: 4, maxRows: 20 },
                            },
                        })}
                    />
                </Box>
                <Box>
                    <Divider />
                </Box>
                <Box>
                    <EditPanelLabel>
                        <Box component={'strong'}>{t('concierge_insight_test_section')}</Box>
                    </EditPanelLabel>
                    <TemplateFormComponent
                        elementProps={insightTestElementProps}
                        component={'InsightPreviewInline'}
                        name={'insight_preview_run'}
                        label={'concierge_insight_test_run'}
                        props={{
                            uid: 'insight-preview-run',
                            element_id: contentSettings.elementId,
                            ai_insight_id: reportAiInsightId,
                            urlPrefix: '/data/editor/report',
                            measurement_time: contentSettings.measurement_time,
                            filters: filtersString,
                            block_component_id: component.datasetRendererBlockComponentId,
                            nameField: 'title',
                        }}
                    />
                </Box>
                <Box>
                    <TemplateFormComponent
                        elementProps={elementProps}
                        component={'FormSwitch'}
                        name={'only_when_significant_ind'}
                        label={t('concierge_insight_only_significant')}
                    />
                    <Box sx={{ pt: 1 }}>
                        <TemplateFormComponent
                            elementProps={elementProps}
                            component={'FormStaticInfo'}
                            name={'only_when_significant_helper'}
                            props={{
                                label:
                                    onlyWhenSignificant === 'Y'
                                        ? t('concierge_insight_significant_yes_helper')
                                        : t('concierge_insight_significant_no_helper'),
                            }}
                        />
                    </Box>
                </Box>

                {onlyWhenSignificant === 'Y' && (
                    <Stack spacing={1}>
                        <TemplateFormComponent
                            elementProps={elementProps}
                            component={'FormTextarea'}
                            name={'fallback_message'}
                            label={t('concierge_insight_fallback')}
                            props={{ placeholder: t('concierge_insight_fallback_ph'), minRows: 2 }}
                            wrapper={true}
                        />
                        <TemplateFormComponent
                            elementProps={elementProps}
                            component={'FormStaticInfo'}
                            name={'fallback_message_helper'}
                            props={{ label: t('llm.fallback_message_helper') }}
                        />
                    </Stack>
                )}
                <Box>
                    <EditPanelLabel>{t('concierge_insight_generation')}</EditPanelLabel>
                    <TemplateFormComponent
                        elementProps={elementProps}
                        component={'FormToggleButtonGroup'}
                        name={'generation_mode'}
                        componentValues={[
                            { label: t('concierge_insight_gen_pregenerate'), value: 'pregenerate' },
                            { label: t('concierge_insight_gen_ondemand'), value: 'on_demand' },
                        ]}
                    />
                    <Box sx={{ pt: 1 }}>
                        <TemplateFormComponent
                            elementProps={elementProps}
                            component={'FormStaticInfo'}
                            name={'generation_mode_helper'}
                            props={{ label: t('llm.generation_helper') }}
                        />
                    </Box>
                </Box>
                <Box>
                    <TemplateFormComponent
                        elementProps={insightTestElementProps}
                        component={'FormStaticLabel'}
                        name={'component_options_section_label'}
                        props={{
                            label: t('llm.component_options'),
                            isSectionLabel: true,
                            translate: false,
                            removeHeaderIndent: true,
                        }}
                    />
                </Box>
                <Stack alignItems={'center'} direction={'row'} spacing={1}>
                    <EditPanelLabel>{t('height')}</EditPanelLabel>
                    <Box sx={{ width: '72px' }}>
                        <ReactHookFormController
                            elementProps={prepareFormElementProps({
                                ...elementProps,
                                inputFilter: 'int',
                                component: { component: 'FormText', name: 'height', label: '' },
                            })}
                        />
                    </Box>
                </Stack>
                <Stack spacing={1}>
                    <EditPanelCheckBox
                        elementProps={elementProps}
                        name={'show_in_viewer'}
                        label={t('show_in_viewer')}
                    />
                    <EditPanelCheckBox
                        elementProps={elementProps}
                        name={'include_in_email'}
                        label={t('include_in_email')}
                    />
                </Stack>
                <Box>
                    <GetEmbedCode contentSettings={contentSettings} height={actualHeight} blockUID={block.uid} t={t} />
                </Box>
            </Stack>
            <Box flexShrink={0}>
                <BlockEditPanelControls
                    onApply={handleApply}
                    onCancel={() => editPanelContext?.closeEditPanel(block.uid, component.settings)}
                />
            </Box>
        </Stack>
    );
}
